// Phase APP-4 · MHM Packer Approvals page — /dashboard/hub/packer-approvals
// Role: main_hub_manager (admin / founder / ceo can also view).
// Reuses KPI strip + queue + drawer + Reject/Approve modals.
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Eye, RefreshCw } from "lucide-react";
import RoleGate from "../../components/RoleGate";
import useTimedFetch from "../../hooks/useTimedFetch";
import StatusBadge from "../../components/hub/StatusBadge";
import AdminApprovalsKPIs from "../../components/admin/approvals/AdminApprovalsKPIs";
import ApprovalDetailDrawer from "../../components/admin/approvals/ApprovalDetailDrawer";
import MHMSubNav from "../../components/hub/MHMSubNav";

const ORANGE = "#FF6B00";
const BUILD_STAMP = "APP-1.0-r7";
const TABS = [
  { id: "pending", label: "Pending Packer Approvals",
    statuses: ["pending", "resubmitted_pending"] },
  { id: "history", label: "History",
    statuses: ["approved", "rejected_terminal", "founder_override_approved", "rejected_1"] },
];

function relTime(iso) {
  if (!iso) return "—";
  const m = Math.floor(Math.max(0, Date.now() - new Date(iso).getTime()) / 60_000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function Body() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlId = searchParams.get("id");
  const [tab, setTab] = useState("pending");
  const [openId, setOpenId] = useState(urlId || null);

  const queryParams = useMemo(() => ({ approval_type: "packer_request" }), []);
  const q = useTimedFetch("/approvals/queue", { params: queryParams });

  useEffect(() => {
    const t = setInterval(q.reload, 30_000);
    return () => clearInterval(t);
  }, [q.reload]);

  useEffect(() => {
    if (openId) setSearchParams({ id: openId });
    else setSearchParams({});
  }, [openId, setSearchParams]);
  useEffect(() => {
    if (urlId) setOpenId(urlId);
    else setOpenId(null);
  }, [urlId]);

  const allRows = q.data?.rows || [];
  const visibleStatuses = TABS.find((t2) => t2.id === tab).statuses;
  const rows = allRows.filter((r) => visibleStatuses.includes(r.status));

  const onChanged = useCallback(() => q.reload(), [q]);

  return (
    <div>
      <MHMSubNav/>
      <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-4"
           data-testid="mhm-packer-approvals-page">
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-2xl font-bold text-slate-900">Packer Approvals</h1>
          <span className="text-xs text-slate-500">
            Approve / reject packer requests submitted by Leads in your main hub.
          </span>
          <span data-testid="mhm-packer-approvals-build-stamp"
                className="ml-auto text-[10px] font-mono text-slate-400">
            {BUILD_STAMP}
          </span>
        </div>

        <AdminApprovalsKPIs/>

        <div className="flex gap-2 border-b border-slate-200">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)}
                    data-testid={`mhm-tab-${t.id}`}
                    aria-current={tab === t.id ? "page" : undefined}
                    className={`text-sm px-3 py-2 -mb-px border-b-2 ${
                      tab === t.id ? "border-[#FF6B00] text-[#FF6B00] font-semibold"
                                    : "border-transparent text-slate-600 hover:text-slate-900"}`}>
              {t.label}
            </button>
          ))}
        </div>

        {q.error ? (
          <div className="bg-rose-50 border border-rose-200 rounded-2xl p-4 text-sm flex items-center"
               data-testid="mhm-pa-error">
            <span className="flex-1">Couldn't load · {q.error.message}</span>
            <button onClick={q.reload}
                    className="text-xs font-semibold px-3 py-1.5 rounded-lg text-white"
                    style={{ backgroundColor: ORANGE }}>Retry</button>
          </div>
        ) : q.loading && rows.length === 0 ? (
          <div className="space-y-2" data-testid="mhm-pa-loading">
            {[0, 1, 2, 3].map((i) => <div key={i} className="h-14 bg-slate-100 rounded-lg animate-pulse"/>)}
          </div>
        ) : rows.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-2xl py-10 text-center"
               data-testid="mhm-pa-empty">
            <RefreshCw className="h-6 w-6 text-slate-300 mx-auto mb-1"/>
            <div className="text-sm font-semibold text-slate-700">No items in this tab</div>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-2xl overflow-x-auto"
               data-testid="mhm-pa-queue-wrap">
            <table className="w-full text-sm min-w-[900px]" data-testid="mhm-pa-queue-table">
              <thead className="bg-slate-50/70">
                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                  <Th>Status</Th><Th>Lead</Th><Th>Micro hub</Th>
                  <Th>Packer</Th><Th>Phone</Th><Th>Shift</Th>
                  <Th>Submitted</Th><Th className="text-center">Resub</Th>
                  <Th className="text-right">Actions</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => {
                  const p = r.entity_payload || {};
                  return (
                    <tr key={r.request_id}
                        data-testid={`mhm-pa-row-${r.request_id}`}
                        data-status={r.status}
                        className="hover:bg-orange-50/30">
                      <td className="px-3 py-2"><StatusBadge status={r.status} size="sm"/></td>
                      <td className="px-3 py-2">{r.submitted_by_name}</td>
                      <td className="px-3 py-2 font-mono text-[11px]">{p.micro_hub_id || "—"}</td>
                      <td className="px-3 py-2 font-medium">{p.full_name || r.display_name}</td>
                      <td className="px-3 py-2 font-mono text-[11px]">{p.phone || "—"}</td>
                      <td className="px-3 py-2 capitalize">{p.shift_timing || "—"}</td>
                      <td className="px-3 py-2 text-xs" title={r.submitted_at}>{relTime(r.submitted_at)}</td>
                      <td className="px-3 py-2 text-center">{r.resubmission_count ?? 0}/1</td>
                      <td className="px-3 py-2 text-right">
                        <button onClick={() => setOpenId(r.request_id)}
                                data-testid={`mhm-pa-view-${r.request_id}`}
                                className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded-lg border border-slate-200 hover:bg-orange-50">
                          <Eye className="h-3 w-3"/> View
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ApprovalDetailDrawer
        open={!!openId} requestId={openId}
        onClose={() => setOpenId(null)}
        onChanged={onChanged}/>
    </div>
  );
}

function Th({ children, className = "" }) {
  return <th className={`px-3 py-2 ${className}`}>{children}</th>;
}

export default function MHMPackerApprovals() {
  return (
    <RoleGate allow={["main_hub_manager", "admin", "founder", "ceo"]}>
      <Body/>
    </RoleGate>
  );
}
